import { useState } from 'react';
import { OverlayTrigger, Tooltip, Button } from 'react-bootstrap';

import { fetchAPI } from '../../Helpers/FetchAPIWrapper';
import { FeatureClickTracker } from '../../Helpers/FeatureHelpers';
import IsLoadingBar from '../isLoading';
import { SupportApps } from '../SmartPanelTabs/SupportApps';
import errorKittie from '../../images/11644-error-animation-entri-app.gif';

import moment from 'moment';
import NamespaceSearchBox from '../Util/NamespaceSearchBox';

const createPodContact = (role, email, name) => {
    return { Role: role, InternalEmailAddress: email, FullName: name };
};

export function DashboardInfo({ dashboardInfo }) {
    return (
        <div style={{lineHeight: '1.2'}}>
            <strong>Namespace:</strong>
            <p>{dashboardInfo.Namespace}</p>
            <strong>ClientId:</strong>
            <p>{dashboardInfo.ClientId}</p>
            <strong>URL:</strong>
            <p><a href={'https://' + dashboardInfo.URL} target='_blank' rel='noopener noreferrer'>{dashboardInfo.URL}</a></p>
            <strong>Version:</strong>
            <p>{dashboardInfo.Version}</p>
            <strong>Prod DB:</strong>
            <p>{dashboardInfo.DatabaseServer}</p>
        </div>
    );
}

export default function Namespace({ passedNamespace, switchSmartPanelTab }) { 
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const [showSupportApps, setShowSupportApps] = useState(false);
    const [dashboardInformationLoading, setDashboardInformationLoading] = useState(false);
    const [namespaceInfo, setNamespaceInfo] = useState(null);
    const [dashboardInformation, setDashboardInformation] = useState(null);    
    
    const getNamespaceInfo = (selectedNamespace) => {
        setIsLoading(true);

        fetchAPI.get('/api/smartPanelClientPod', {
            headers: {
                namespace: selectedNamespace,
            }
        })
        .then(namespaceData => {
            const podContacts = [
                createPodContact('Pod Lead', namespaceData.PodLead_Email, namespaceData.PodLead),
                createPodContact('Pod Manager', namespaceData.PodManager_Email, namespaceData.PodManager),
                createPodContact('CSM', namespaceData.CSM_Email, namespaceData.CSM),
                createPodContact('TAM', namespaceData.TAM_Email, namespaceData.TAM),
                createPodContact('SDM', namespaceData.SDM_Email, namespaceData.SDM),
                createPodContact('Tax Rep', namespaceData.TaxRep_Email, namespaceData.TaxRep),
            ];
            namespaceData.podContacts = podContacts;
            
            setNamespaceInfo(namespaceData);
            setDashboardInformation(null);
            setIsLoading(false);
        }).catch(error => {
            window.apiError(error);
        });
    };

    const getDashboardInfo = () => {
        setDashboardInformationLoading(true);
        fetchAPI.get('/api/getDashboardInfo', {
            headers: {
                namespace: namespaceInfo.namespace,
            }
        })
        .then(dashboardInfo => {
            setDashboardInformation(dashboardInfo[0]);
            setDashboardInformationLoading(false);
        }).catch(error => {
            setDashboardInformationLoading(false);
            window.apiError(error);
        });
    };   

    const getActiveCaseDownload = () => {
        setIsExporting(true);

        fetchAPI.get('/api/ActiveCasesToExcel', {
            headers: {
                namespace: namespaceInfo.namespace,
            }
        }).then(activeCases => {
            const str = activeCases.excelData;
            const bytes = new TextEncoder().encode(str);
            const blob = new Blob([bytes], {
                type: 'application/json;charset=utf-8'
            });
            const href = window.URL.createObjectURL(blob);
            var element = document.createElement('a');
            element.setAttribute('href', href);
            element.setAttribute('download', `${namespaceInfo.namespace}${moment().format('YYYYMMDD')}.csv`);

            element.style.display = 'none';
            document.body.appendChild(element);

            element.click();

            document.body.removeChild(element);
            FeatureClickTracker('Export Active Cases');
            setIsExporting(false);
        }).catch(error => {
            window.apiError(error);
            setIsExporting(false);
        });

    };

    return (
        <div>           
            <NamespaceSearchBox passedNamespace={passedNamespace} getNamespaceInfo={getNamespaceInfo} />

            {(!namespaceInfo && !isLoading) ? 
                <div>
                    <img className='error-kittie' src={errorKittie} alt='no namespaces found with that name' />
                    <h6 className='no-namespace-wording '>No namespace found. Use the search bar above</h6>
                </div>
                :
                isLoading ?
                    <IsLoadingBar alttext='Namespaces are loading' />
                :
                <>
                    <div className='section-2 p-4'>
                        <h4>{namespaceInfo.ClientName}</h4>
                        <h6>{namespaceInfo.Namespace}</h6>
                        {namespaceInfo.podContacts.map((item, index) => 
                            <p key={index}>
                                <strong>{item.Role ? item.Role + ': ' : ''}</strong>
                                <a href={`mailto:${item.InternalEmailAddress}`}>{item.FullName}</a>
                            </p>
                        )}
                        <p><strong>Support Pod: </strong><span>{namespaceInfo.SupportPod}</span></p>
                        <p><strong>Transition To Support Date: </strong><span>{namespaceInfo.TransitionToSupportDate}</span></p>
                        <p><strong>Release Upgrade Date: </strong>
                            <span><Button variant='link' className='p-0 pl-1' onClick={() => switchSmartPanelTab('UPD', namespaceInfo.namespace)}><small>See Upgrade Date</small></Button></span>
                            <OverlayTrigger
                                placement='top'
                                overlay={
                                    <Tooltip>
                                        Request Date Change
                                    </Tooltip>
                                }>
                                <Button variant='link' className='p-0 pl-1' onClick={() => switchSmartPanelTab('UPD', namespaceInfo.namespace)}><i className='far fa-clock'></i></Button> 
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement='bottom'
                                overlay={
                                    <Tooltip>
                                        Upgrade information based on client pod site locations (e.g., US/CAN - EDT/EST, EUR - BST/GMT, AUS - AEST/AEDT, etc.)
                                    </Tooltip>
                                }
                                >
                                <span className='m-0'><i className='far fa-question-circle tooltip-icon'></i></span>
                            </OverlayTrigger>   
                        </p>
                        <p><strong>Number of Employees: </strong><span>{namespaceInfo.NumberEmployees}</span></p>
                        <p><strong>Active Cases: </strong>
                            <Button className='p-0' variant='link' onClick={() => getActiveCaseDownload()} disabled={isExporting}><small><strong>{isExporting ? 'Exporting' : 'Export'}</strong></small></Button>
                            <OverlayTrigger
                                placement='top'
                                overlay={
                                    <Tooltip>
                                        Click to get detailed Active cases (downloads in excel)
                                    </Tooltip>
                                }
                                >
                                <span className='m-2'><i className='far fa-question-circle tooltip-icon'></i></span>
                            </OverlayTrigger>
                        </p>
                    </div>
                    <section className='section-3 px-4'>
                        <h5 className='mb-0 text-dark'>
                            Environment Info
                            <Button variant='link' onClick={() => setShowSupportApps(!showSupportApps)} ><i className='fas fa-tools'></i></Button>
                        </h5>
                            
                        {!dashboardInformation && (
                            <Button onClick={() => getDashboardInfo()} variant='link' className='p-0'><small>Get Environment Info</small></Button>
                        )}

                        {dashboardInformationLoading && (
                            <IsLoadingBar alttext='Environment Info is loading' />
                        )}

                        {showSupportApps && (
                            <SupportApps namespace={namespaceInfo.namespace} SupportApps={namespaceInfo.namespace} />
                        )}
                        
                        {dashboardInformation && (
                            <DashboardInfo dashboardInfo={dashboardInformation} />
                        )}                  
        
                    </section>
                </>
            }          
        </div>
    );
}